import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { TrendingUp, RefreshCw, Lock, Search } from "lucide-react";
import StockTable, { DEFAULT_VISIBLE_COLUMNS } from "@/components/StockTable";
import StatCard from "@/components/StatCard";
import { fmtM, fmtPct } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_RELOAD_MS = 10 * 60 * 1000;

function useShareData(token, period) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/report/${token}`, { params: { period } });
      setData(res.data); setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "This report link is not available.");
    } finally { setLoading(false); }
  }, [token, period]);
  useEffect(() => { load(); const t = setInterval(load, AUTO_RELOAD_MS); return () => clearInterval(t); }, [load]);
  return { data, error, loading, load };
}

function Unavailable({ message }) {
  return (
    <div data-testid="share-unavailable" className="min-h-screen flex items-center justify-center bg-[#F1F6F8] px-6">
      <div className="max-w-md text-center bg-white border border-[#D2E0E6] rounded-xl p-10">
        <Lock className="w-8 h-8 text-[#7C949C] mx-auto mb-4" />
        <h1 className="text-xl font-bold text-[#0B2830] mb-2">Report unavailable</h1>
        <p className="text-sm text-[#47616B]">{message} Please ask Quantum Leap Solutions for an updated link.</p>
      </div>
    </div>
  );
}

function PeriodToggle({ period, setPeriod }) {
  return (
    <div className="flex bg-white border border-[#D2E0E6] rounded-md p-0.5">
      {["quarterly", "annual"].map((p) => (
        <button key={p} data-testid={`share-period-${p}`} onClick={() => setPeriod(p)}
          className={`px-3 py-1.5 text-sm rounded capitalize transition-colors ${period === p ? "bg-[#0284C7] text-white" : "text-[#47616B] hover:text-[#0B2830]"}`}>
          {p}
        </button>
      ))}
    </div>
  );
}

export default function PublicShareReport() {
  const token = window.location.pathname.split("/share/")[1]?.split("/")[0] || "";
  const [period, setPeriod] = useState("quarterly");
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const { data, error, loading, load } = useShareData(token, period);

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const list = data.stocks.filter((s) => !q || s.ticker.toLowerCase().includes(q) || (s.name || "").toLowerCase().includes(q));
    return list.sort((a, b) => {
      const av = a[sortKey] ?? -Infinity, bv = b[sortKey] ?? -Infinity;
      const cmp = typeof av === "string" || typeof bv === "string" ? String(av).localeCompare(String(bv)) : av - bv;
      return sortDir === "desc" ? -cmp : cmp;
    });
  }, [data, search, sortKey, sortDir]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    const yoys = rows.map((s) => s.ebitda_yoy).filter((v) => v != null);
    return {
      avgYoy: yoys.reduce((a, b) => a + b, 0) / (yoys.length || 1),
      totalBuyback: rows.reduce((a, b) => a + (b.buyback_12m_m || 0), 0),
      beats: rows.filter((s) => s.beats_peers).length,
    };
  }, [rows]);

  if (error && !data) return <Unavailable message={error} />;
  const updated = data?.last_updated ? new Date(data.last_updated).toLocaleString() : "\u2014";

  return (
    <div data-testid="public-share-report" className="min-h-screen bg-[#F1F6F8]">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-8">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-[#0284C7] mb-1">
              <TrendingUp className="w-5 h-5" />
              <span className="mono text-xs uppercase tracking-[0.25em]">Quantum Leap Solutions &middot; Today's Stock Pick</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0B2830]">Global Value & Buyback Screener</h1>
            <p data-testid="share-last-updated" className="text-sm text-[#47616B] mt-1">
              US-listed stocks (S&P 500) above $10B market cap with positive annual EBITDA growth &middot; refreshed daily &middot; Last updated {updated}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7C949C]" />
              <input data-testid="share-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticker"
                className="pl-8 pr-3 py-2 text-sm bg-white border border-[#D2E0E6] rounded-md focus:outline-none focus:border-[#0284C7] w-40" />
            </div>
            <PeriodToggle period={period} setPeriod={setPeriod} />
            <button data-testid="share-reload" onClick={load} disabled={loading}
              className="flex items-center gap-1.5 bg-[#0284C7] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reload
            </button>
          </div>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard testid="share-stat-count" label="Companies screened" accent="#0284C7"
            value={rows.length} sub={`${rows.filter((s) => s.qualifies).length} fully qualify`} />
          <StatCard testid="share-stat-growth" label="Avg EBITDA YoY" accent="#059669" value={stats ? fmtPct(stats.avgYoy) : "\u2014"} sub="across shown names" />
          <StatCard testid="share-stat-buybacks" label="Total 12M buybacks" accent="#D97706" value={stats ? fmtM(stats.totalBuyback) : "\u2014"} sub="sum, USD" />
          <StatCard testid="share-stat-beats" label="Beating peers" accent="#0D9488" value={stats ? `${stats.beats}/${rows.length}` : "\u2014"} sub="below-median P/E + above-median growth" />
        </div>

        <StockTable stocks={rows} sortKey={sortKey} sortDir={sortDir} onSort={onSort} onRowClick={() => {}} visibleColumns={DEFAULT_VISIBLE_COLUMNS} />

        <p data-testid="share-disclaimer" className="text-xs text-[#7C949C] mt-4 max-w-3xl leading-relaxed">
          For informational purposes only, not investment advice. Financials sourced from free public data (stockanalysis.com).
          {data?.risk_disclaimer ? ` ${data.risk_disclaimer}` : ""}
        </p>
      </div>
    </div>
  );
}
